import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import type { GraphWidth } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

/**
 * One LibreNMS graph. The proxy endpoint needs the same Authorization header
 * as every other call, which an <img src> cannot send — so the SVG is fetched
 * through the api client and rendered from a blob URL that is revoked when the
 * window changes or the dialog closes (unmount).
 */
export function GraphImg({ queryKey, fetch, alt }: { queryKey: unknown[]; fetch: (width: GraphWidth) => Promise<Blob>; alt: string }) {
  // Nearest whitelisted render width to the container, measured once on mount
  // so the SVG is crisp at card width instead of upscaled from 800.
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<GraphWidth | null>(null);
  useEffect(() => {
    const w = ref.current?.clientWidth ?? 800;
    setWidth(w > 1400 ? 1600 : w > 1000 ? 1200 : 800);
  }, []);
  const { data, error, isFetching } = useQuery({
    queryKey: [...queryKey, width],
    queryFn: () => fetch(width!),
    enabled: width !== null,
    retry: false,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!data) return;
    const u = URL.createObjectURL(data);
    setUrl(u);
    return () => { URL.revokeObjectURL(u); setUrl(null); };
  }, [data]);
  let body: ReactNode;
  if (error) body = <p className="text-xs text-rose-300">{t("graph.unavailable", { msg: (error as Error).message })}</p>;
  else if (!url) body = <p className="text-xs text-slate-500">{t(isFetching || !width ? "graph.loading" : "graph.none")}</p>;
  // LibreNMS draws for a light page: its legend and axes vanish on slate-950,
  // so the image sits on a light card and scales to the container width.
  else body = <img src={url} alt={alt} className="block h-auto w-full" />;
  return <div ref={ref} className={url ? "rounded-md border border-slate-300 bg-white p-1" : undefined}>{body}</div>;
}
